import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/company/
import { useCompanyContext } from '@/hooks/useCompanyContext';
import { useCompanyRole } from '@/hooks/useCompanyRole';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { FileText } from 'lucide-react';

// LOCAL_TYPE_JUSTIFIED: common shape across two query return types for policy list items
interface IPolicyListItem {
  _id: string;
  policyNumber: string;
  status: string;
  insuranceTypeName?: string;
  employeeName?: string;
  issuedAt?: number;
  expiresAt?: number;
  createdAt: number;
}

const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  draft: 'secondary',
  active: 'default',
  expired: 'outline',
  cancelled: 'destructive',
};

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function PolicyTableSkeleton() {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center px-6 py-3 border-b border-table-row-border gap-4">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-36 ml-4" />
          <Skeleton className="h-4 w-28 ml-4" />
          <Skeleton className="h-4 w-20 ml-4" />
          <Skeleton className="h-4 w-24 ml-4" />
          <Skeleton className="h-4 w-24 ml-auto" />
        </div>
      ))}
    </>
  );
}

export function CompanyPoliciesListPage() {
  const navigate = useNavigate();
  const { currentCompany, isLoading: companyLoading } = useCompanyContext();
  const { canViewAllPolicies } = useCompanyRole();
  const [searchQuery, setSearchQuery] = useState('');

  const isFullAccess = canViewAllPolicies;

  // Get employeeRecordId from current membership for employee-role users
  const currentMembership = useQuery(api.companies.members.getCurrentMembership);
  const employeeRecordId = currentMembership?.employeeRecordId;

  // Admin/Manager: all company policies; Employee: own policies only
  const allCompanyPolicies = useQuery(
    api.insurance.policies.listByCompany,
    currentCompany && isFullAccess ? { companyId: currentCompany._id } : 'skip'
  );
  const ownPolicies = useQuery(
    api.insurance.policies.listByCompanyEmployee,
    !isFullAccess && employeeRecordId ? { companyEmployeeId: employeeRecordId } : 'skip'
  );

  const rawPolicies: IPolicyListItem[] | undefined = isFullAccess
    ? allCompanyPolicies
    : ownPolicies;
  const isLoading = companyLoading || rawPolicies === undefined;
  const policies = useMemo(() => rawPolicies ?? [], [rawPolicies]);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return policies;
    const q = searchQuery.toLowerCase();
    return policies.filter(
      (p: IPolicyListItem) =>
        p.policyNumber.toLowerCase().includes(q) ||
        (p.employeeName && p.employeeName.toLowerCase().includes(q))
    );
  }, [policies, searchQuery]);

  if (!companyLoading && !currentCompany) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No company found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of a company to view policies.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Policies</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading
              ? 'Loading...'
              : isFullAccess
                ? `${policies.length} company policies`
                : `${policies.length} your policies`}
          </p>
        </div>
      </div>

      <div className="bg-table-container border border-table-container-border rounded-2xl overflow-hidden shadow">
        <div className="flex items-center gap-3 px-6 py-3 border-b border-table-row-border">
          <label htmlFor="policy-search" className="sr-only">
            Search policies
          </label>
          <Input
            id="policy-search"
            placeholder="Search by policy # or employee..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="max-w-xs"
          />
        </div>

        {/* Table header */}
        <div className="flex items-center px-6 py-2 border-b border-table-row-border bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wider">
          <span className="w-32 shrink-0">Policy #</span>
          {isFullAccess && <span className="w-44 shrink-0">Employee</span>}
          <span className="w-36 shrink-0">Type</span>
          <span className="w-24 shrink-0">Status</span>
          <span className="w-28 shrink-0">Start Date</span>
          <span className="flex-1">Expiry Date</span>
        </div>

        <div className="overflow-auto">
          {isLoading && <PolicyTableSkeleton />}

          {!isLoading &&
            filtered.map((policy: IPolicyListItem) => (
              <div
                key={policy._id}
                className="flex items-center px-6 py-3 border-b border-table-row-border hover:bg-muted/50 cursor-pointer"
                onClick={() => navigate(`/company/policies/${policy._id}`)}
              >
                <span className="w-32 shrink-0 text-sm font-medium text-foreground truncate pr-3">
                  {policy.policyNumber}
                </span>
                {isFullAccess && (
                  <span className="w-44 shrink-0 text-sm text-muted-foreground truncate pr-3">
                    {policy.employeeName ?? '---'}
                  </span>
                )}
                <span className="w-36 shrink-0 text-sm text-muted-foreground truncate pr-3">
                  {policy.insuranceTypeName ?? '---'}
                </span>
                <span className="w-24 shrink-0">
                  <Badge
                    variant={STATUS_VARIANTS[policy.status] || 'secondary'}
                    className="capitalize"
                  >
                    {policy.status}
                  </Badge>
                </span>
                <span className="w-28 shrink-0 text-sm text-muted-foreground">
                  {policy.issuedAt ? formatDate(policy.issuedAt) : '---'}
                </span>
                <span className="flex-1 text-sm text-muted-foreground">
                  {policy.expiresAt ? formatDate(policy.expiresAt) : '---'}
                </span>
              </div>
            ))}

          {!isLoading && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">No policies found</h3>
              <p className="text-sm text-muted-foreground">
                {searchQuery
                  ? 'Try adjusting your search query'
                  : 'No policies are linked to this company yet'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
